/** @module @liquidefy/code-editor/global/global-events @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  EditorEventData,
  EditorEventBus,
  EventChannel,
  EventChannelId,
  EditorEventDomain,
  EditorEventKind,
  Timestamp,
} from '../code-editor-types.js';

import {
  ok,
  err,
  now,
  asEventChannelId,
} from '../code-editor.js';

// ═══ §2 EVENT CREATION — factory functions for EditorEventData ═══

export function createEvent(
  channel: EventChannelId,
  type: string,
  payload: unknown,
  source: string,
  priority?: 'low' | 'normal' | 'high',
): EditorEventData {
  return {
    channel,
    type,
    payload,
    timestamp: now(),
    source,
    priority: priority ?? 'normal',
  };
}

export function createHighPriorityEvent(
  channel: EventChannelId,
  type: string,
  payload: unknown,
  source: string,
): EditorEventData {
  return createEvent(channel, type, payload, source, 'high');
}

export function createLowPriorityEvent(
  channel: EventChannelId,
  type: string,
  payload: unknown,
  source: string,
): EditorEventData {
  return createEvent(channel, type, payload, source, 'low');
}

export function eventKey(event: EditorEventData): string {
  return `${event.channel}:${event.type}:${event.timestamp}`;
}

// ═══ §3 CHANNEL MANAGEMENT — create, update, query channels ═══

export function createChannel(
  id: string,
  name: string,
  pattern: string,
): EventChannel {
  return {
    id: asEventChannelId(id),
    name,
    pattern,
    listeners: 0,
    lastEvent: undefined,
  };
}

export function updateChannel(
  channel: EventChannel,
  event: EditorEventData,
): EventChannel {
  return {
    ...channel,
    lastEvent: event.timestamp,
    listeners: channel.listeners,
  };
}

export function channelMatches(channel: EventChannel, eventType: string): boolean {
  return matchEventPattern(channel.pattern, eventType);
}

export function isChannelActive(channel: EventChannel, maxAge?: number): boolean {
  if (channel.lastEvent === undefined) return false;
  const age = maxAge ?? 60000;
  return (now() - channel.lastEvent) < age;
}

export function channelStats(channel: EventChannel): {
  listeners: number;
  lastEvent: number | undefined;
  age: number | undefined;
} {
  return {
    listeners: channel.listeners,
    lastEvent: channel.lastEvent,
    age: channel.lastEvent !== undefined ? now() - channel.lastEvent : undefined,
  };
}

// ═══ §4 EVENT BUS — create and manage the bus and its channels ═══

export function createEventBus(): EditorEventBus {
  return { channels: new Map() };
}

export function addChannel(bus: EditorEventBus, channel: EventChannel): EditorEventBus {
  const channels = new Map(bus.channels);
  channels.set(channel.id, channel);
  return { channels };
}

export function removeChannel(bus: EditorEventBus, channelId: EventChannelId): EditorEventBus {
  const channels = new Map(bus.channels);
  channels.delete(channelId);
  return { channels };
}

export function getChannel(bus: EditorEventBus, channelId: EventChannelId): EventChannel | undefined {
  return bus.channels.get(channelId);
}

export function listChannels(bus: EditorEventBus): EventChannel[] {
  return Array.from(bus.channels.values());
}

export function channelCount(bus: EditorEventBus): number {
  return bus.channels.size;
}

export function activeChannels(bus: EditorEventBus, maxAge?: number): EventChannel[] {
  return listChannels(bus).filter(ch => isChannelActive(ch, maxAge));
}

// ═══ §5 PATTERN MATCHING — wildcard and glob patterns for event routing ═══

export const WILDCARD_ALL = '*';
export const GLOB_SEPARATOR = '.';

export function isWildcard(pattern: string): boolean {
  return pattern === '*' || pattern === '**';
}

export function isGlobPattern(pattern: string): boolean {
  return pattern.includes('*') || pattern.includes('?');
}

export function compilePattern(pattern: string): RegExp {
  if (pattern === '*') return /^[^.]+$/;
  if (pattern === '**') return /^.+$/;

  let regexStr = '^';
  let i = 0;
  while (i < pattern.length) {
    const ch = pattern[i]!;
    if (ch === '*') {
      if (i + 1 < pattern.length && pattern[i + 1] === '*') {
        regexStr += '.*';
        i += 2;
        if (i < pattern.length && pattern[i] === '.') {
          regexStr += '\\.';
          i++;
        }
      } else {
        regexStr += '[^.]*';
        i++;
      }
    } else if (ch === '?') {
      regexStr += '[^.]';
      i++;
    } else if (ch === '.') {
      regexStr += '\\.';
      i++;
    } else if ('.+^${}()|[]\\'.includes(ch)) {
      regexStr += '\\' + ch;
      i++;
    } else {
      regexStr += ch;
      i++;
    }
  }
  regexStr += '$';
  return new RegExp(regexStr);
}

export function matchEventPattern(pattern: string, eventType: string): boolean {
  if (pattern === '**') return true;
  if (pattern === '*') return !eventType.includes('.');
  if (!isGlobPattern(pattern)) return pattern === eventType;
  const regex = compilePattern(pattern);
  return regex.test(eventType);
}

export function specificity(pattern: string): number {
  if (pattern === '**') return 0;
  if (pattern === '*') return 1;

  let score = 0;
  const parts = pattern.split('.');
  for (const part of parts) {
    if (part === '**') score += 1;
    else if (part === '*') score += 2;
    else if (part.includes('*') || part.includes('?')) score += 3;
    else score += 4;
  }
  return score;
}

// ═══ §6 EVENT FILTERING — filter, group, and query event collections ═══

export function filterEvents(
  events: EditorEventData[],
  predicate: (event: EditorEventData) => boolean,
): EditorEventData[] {
  return events.filter(predicate);
}

export function filterByChannel(
  events: EditorEventData[],
  channelId: EventChannelId,
): EditorEventData[] {
  return events.filter(e => e.channel === channelId);
}

export function filterByType(
  events: EditorEventData[],
  type: string,
): EditorEventData[] {
  return events.filter(e => e.type === type);
}

export function filterBySource(
  events: EditorEventData[],
  source: string,
): EditorEventData[] {
  return events.filter(e => e.source === source);
}

export function filterByPriority(
  events: EditorEventData[],
  minPriority: 'low' | 'normal' | 'high',
): EditorEventData[] {
  const priorityOrder: Record<string, number> = { low: 0, normal: 1, high: 2 };
  const threshold = priorityOrder[minPriority] ?? 0;
  return events.filter(e => (priorityOrder[e.priority] ?? 0) >= threshold);
}

export function filterByTimeRange(
  events: EditorEventData[],
  start: number,
  end: number,
): EditorEventData[] {
  return events.filter(e => e.timestamp >= start && e.timestamp <= end);
}

export function eventsByChannel(
  events: EditorEventData[],
): Map<EventChannelId, EditorEventData[]> {
  const result = new Map<EventChannelId, EditorEventData[]>();
  for (const event of events) {
    const existing = result.get(event.channel) ?? [];
    existing.push(event);
    result.set(event.channel, existing);
  }
  return result;
}

export function eventsByType(
  events: EditorEventData[],
): Map<string, EditorEventData[]> {
  const result = new Map<string, EditorEventData[]>();
  for (const event of events) {
    const existing = result.get(event.type) ?? [];
    existing.push(event);
    result.set(event.type, existing);
  }
  return result;
}

// ═══ §7 EVENT SERIALIZATION — serialize/deserialize, batch, logging ═══

export function serializeEvent(event: EditorEventData): Record<string, unknown> {
  return {
    channel: event.channel,
    type: event.type,
    payload: event.payload,
    timestamp: event.timestamp,
    source: event.source,
    priority: event.priority,
  };
}

export function deserializeEvent(data: Record<string, unknown>): EditorEventData {
  return {
    channel: asEventChannelId(data['channel'] as string),
    type: data['type'] as string,
    payload: data['payload'],
    timestamp: (data['timestamp'] as number) ?? now(),
    source: (data['source'] as string) ?? 'unknown',
    priority: (data['priority'] as 'low' | 'normal' | 'high') ?? 'normal',
  };
}

export function eventToString(event: EditorEventData): string {
  const ts = new Date(event.timestamp).toISOString();
  return `[${ts}] [${event.priority.toUpperCase()}] ${event.channel}/${event.type} (${event.source})`;
}

export function batchEvents(events: EditorEventData[]): Record<string, unknown> {
  return {
    type: 'event-batch',
    count: events.length,
    timestamp: now(),
    events: events.map(serializeEvent),
  };
}

export function unbatchEvents(batch: Record<string, unknown>): EditorEventData[] {
  const events = batch['events'] as Record<string, unknown>[];
  if (!Array.isArray(events)) return [];
  return events.map(deserializeEvent);
}

export const EVENT_DOMAINS: readonly EditorEventDomain[] = [
  'document', 'cursor', 'history', 'marker', 'tokenizer', 'language',
  'completion', 'diagnostic', 'navigation', 'presentation', 'layout',
  'widget', 'editor', 'find', 'snippet', 'command', 'accessibility',
  'terminal', 'pty', 'buffer', 'ansi', 'output', 'session',
  'multiplexer', 'recording', 'collaboration', 'cloud', 'ai', 'agent',
  'conversation', 'codegen', 'analysis', 'model', 'cache',
  'personalization', 'security', 'workspace', 'git', 'filesystem',
  'project', 'plugin', 'settings', 'diff', 'review', 'adapter',
  'global', 'io',
] as const;

export const STANDARD_EVENTS: Record<string, readonly string[]> = {
  document: ['created', 'opened', 'changed', 'closed', 'deleted', 'renamed', 'synced'],
  cursor: ['moved', 'changed', 'updated'],
  history: ['changed', 'created', 'deleted'],
  editor: ['activated', 'deactivated', 'created', 'closed', 'changed'],
  terminal: ['created', 'started', 'stopped', 'closed', 'changed', 'connected', 'disconnected'],
  completion: ['requested', 'responded', 'completed', 'cancelled'],
  diagnostic: ['created', 'updated', 'deleted', 'changed'],
  workspace: ['opened', 'closed', 'changed', 'synced'],
  git: ['changed', 'synced', 'conflicted', 'resolved'],
  plugin: ['activated', 'deactivated', 'started', 'stopped', 'failed'],
  ai: ['requested', 'responded', 'completed', 'failed', 'cancelled'],
  settings: ['changed', 'updated', 'synced'],
  layout: ['changed', 'updated'],
  global: ['started', 'stopped', 'changed', 'failed'],
};
